import React, { ReactNode, forwardRef } from 'react';

/**
 * Nav Item Footer and Main
 */
export type LinkProps = {
  href?: string;
  url: string;
  type?: string;
  linktype?: string;
  linkType?: string; // This one should be the correct one
  target?: string;
  rel?: string;
};

type LinkComponentProps = {
  to?: string;
  href?: string;
  target?: string;
  as?: string;
  ref?: React.Ref<HTMLElement>;
  className?: string;
  onClick?: (event: Event) => void;
  rel?: string;
};

export type NavItemProps = {
  children: ReactNode;
  StyledLink: React.ComponentType<LinkComponentProps>;
  link: LinkProps;
  ariaLabelledBy?: string;
  className?: string;
  tabIndex?: string | number;
  additionalStyledLinkProps?: any;
  disabled?: boolean;
  tag?: string;
  variant?: string;
  onClick?: () => void;
  lang?: string;
};

export type Ref = HTMLElement;

const NavItem = forwardRef<Ref, NavItemProps>(
  (
    {
      children,
      StyledLink,
      link,
      ariaLabelledBy,
      className,
      additionalStyledLinkProps = {},
      disabled,
      tag,
      variant,
      ...rest
    },
    ref
  ) => {
    if (!children) {
      return null;
    }

    const props: LinkComponentProps = {};

    if (ariaLabelledBy) {
      props['aria-labelledby'] = ariaLabelledBy;
    }

    if (disabled) {
      props['data-disabled'] = true;
    }

    if (link && !disabled) {
      props.target = link.target || null;

      const regexp = new RegExp("^https?://", "i");

      const linkType = link?.type || link?.linktype || link?.linkType || null;

      if (linkType && (linkType.toLowerCase() === 'external' || linkType.toLowerCase() === 'mailto' || linkType.toLowerCase() === 'anchor' || linkType.toLowerCase() === 'javascript' || ((linkType.toLowerCase() === 'internal' || linkType.toLowerCase() === 'media') && regexp.test(link.url)))) {
        props.as = tag || 'a';
        props.href = link.url;
      } else {
        // internal link (default)
        props.to = link.href || link.url;
      }

      if (link.rel) {
        props.rel = link.rel;
      } else if (link?.target === '_blank' && !link.rel) {
        // information disclosure attack prevention keeps target blank site from getting ref to window.opener
        props.rel = 'noopener noreferrer';
      }
    } else {
      props.as = 'span';
    }

    return (
      <StyledLink
        className={className}
        ref={ref}
        {...props}
        {...rest}
        {...additionalStyledLinkProps}
      >
        {children}
      </StyledLink>
    );
  }
);

export default NavItem;
